import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  InputLabel,
  FilledInput,
  Stepper,
  Step,
  StepLabel,
  Link,
  Snackbar,
  TableFooter,
  TablePagination,
  Alert,
} from "@mui/material/";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import ScaleLoader from "react-spinners/ScaleLoader";

import dayjs from "dayjs";
import { format } from "date-fns";
import randomString from "randomstring";
import Confetti from "react-confetti";

import * as actions from "../../redux/actions/index";
import { TablePaginationActions } from "../../components/TablePaginationActions/TablePaginationActions.component";

const raffleSteps = () => {
  return ["Search Entries", "Draw Winners"];
};

const FIRST_PAGE = 0;
const PAGE_SIZE_OPTIONS = [25, 30, 50];

const Raffle = () => {
  const dispatch = useDispatch();
  const steps = raffleSteps();
  const cmsAuth = useSelector((state) => state.cmsAuthenticator.cmsAuth || "");
  const entries = useSelector((state) => state.cmsRaffle.entries);
  const total = useSelector((state) => state.cmsRaffle.total || 0);
  const winners = useSelector((state) => state.cmsRaffle.winners);
  const savedWinners = useSelector((state) => state.cmsRaffle.savedWinners);
  const searchEntriesLoading = useSelector(
    (state) => state.cmsRaffle.searchEntriesLoading
  );
  const raffleDrawLoading = useSelector(
    (state) => state.cmsRaffle.raffleDrawLoading
  );
  const [startRaffleDate, setStartRaffleDate] = useState(null);
  const [endRaffleDate, setEndRaffleDate] = useState(null);
  const [numOfWinners, setNumOfWinners] = useState("");
  const [nonWinners, setNonWinners] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [saved, setSaved] = useState(false);
  const session = window.sessionStorage.getItem("tangSessionToken");
  const [page, setPage] = useState(FIRST_PAGE);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE_OPTIONS[0]);
  const [successfulWinners, setSuccessfulWinners] = useState(false);
  const role = cmsAuth.role.toLowerCase().replace("_", "");

  useEffect(() => {
    dispatch(actions.onResetEntriesWinners());
  }, [dispatch]);

  const onFormatDateChangedHandler = (dateArg, which) => {
    if (dateArg) {
      const date = dayjs(dateArg).format("YYYY-MM-DD HH:mm");

      const dateWithSeconds = dayjs(dateArg).second(59);
      const endDateFormat = dateWithSeconds.format("YYYY-MM-DD HH:mm");

      if (which === "start") {
        setStartRaffleDate(date);
      } else {
        setEndRaffleDate(endDateFormat);
      }
    } else {
      if (which === "start") {
        setStartRaffleDate(null);
      } else {
        setEndRaffleDate(null);
      }
    }
  };

  const onSearchRaffleEntries = () => {
    const token = randomString.generate({
      length: 7,
    });

    window.sessionStorage.setItem("tangSessionToken", token);
    const data = JSON.stringify({
      session: token,
      start_date: startRaffleDate,
      end_date: endRaffleDate,
      non_winners_only: nonWinners,
    });
    dispatch(
      actions.onSearchEntries(data, FIRST_PAGE, "search", rowsPerPage, role)
    );
    setSaved(false);
  };

  const onRaffleDraw = () => {
    const token = window.sessionStorage.getItem("tangSessionToken");
    const data = JSON.stringify({
      session: token,
      num_of_winners: parseInt(numOfWinners),
    });
    dispatch(actions.onRaffleDraw(data, setSuccessfulWinners, role));
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const resetStep = () => {
    setActiveStep(0);
    setSaved(false);
    dispatch(actions.onResetEntriesWinners());
  };

  const handleChangePage = (event, newPage) => {
    const token = window.sessionStorage.getItem("tangSessionToken");
    const data = JSON.stringify({
      session: token,
      start_date: startRaffleDate,
      end_date: endRaffleDate,
      non_winners_only: nonWinners,
    });

    setPage(newPage);
    dispatch(actions.onSearchEntries(data, newPage, "page", rowsPerPage, role));
  };

  const handleChangeRowsPerPage = (event) => {
    const token = window.sessionStorage.getItem("tangSessionToken");
    const data = JSON.stringify({
      session: token,
      start_date: startRaffleDate,
      end_date: endRaffleDate,
      non_winners_only: nonWinners,
    });
    setRowsPerPage(parseInt(event.target.value));
    setPage(FIRST_PAGE);
    dispatch(
      actions.onSearchEntries(
        data,
        FIRST_PAGE,
        "page",
        parseInt(event.target.value),
        role
      )
    );
  };

  // EXPORT ENTRIES & RAFFLE DRAW
  const entriesExport = (e) => {
    e.preventDefault();
    return dispatch(actions.onEntriesExport(session, role));
  };

  // EXPORT ENTRIES
  const raffleDrawExport = (e) => {
    e.preventDefault();
    return dispatch(actions.onRaffleDrawExport(session, role));
  };

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return (
          <>
            <form className="Raffle__inputs">
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  value={
                    startRaffleDate == null
                      ? startRaffleDate
                      : dayjs(startRaffleDate)
                  }
                  label="Starting Raffle Date"
                  onChange={(d) => onFormatDateChangedHandler(d, "start")}
                />
              </LocalizationProvider>

              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DateTimePicker
                  value={
                    endRaffleDate == null ? endRaffleDate : dayjs(endRaffleDate)
                  }
                  label="Ending Raffle Date"
                  onChange={(d) => onFormatDateChangedHandler(d, "end")}
                />
              </LocalizationProvider>

              <FormControl className="Raffle__inputs-group">
                <FormControlLabel
                  control={
                    <Checkbox
                      size="medium"
                      checked={nonWinners}
                      color="primary"
                      onChange={(e) => setNonWinners(e.target.checked)}
                    />
                  }
                  label="Exclude Past Winners"
                />
              </FormControl>
            </form>

            {searchEntriesLoading ? (
              <div style={{ textAlign: "center", marginTop: "5rem" }}>
                <ScaleLoader
                  loading={searchEntriesLoading}
                  height={50}
                  width={25}
                  margin={2.5}
                  color="#3d5af1"
                />
              </div>
            ) : (
              <Button
                style={{
                  width: "50%",
                  margin: "2rem auto 1.5rem",
                  height: "5rem",
                }}
                onClick={onSearchRaffleEntries}
                color="primary"
                disabled={startRaffleDate === null || endRaffleDate === null}
                variant="contained"
              >
                SEARCH ENTRIES
              </Button>
            )}

            {entries.length > 0 && (
              <Button
                style={{
                  width: "50%",
                  margin: ".5rem auto 1.5rem",
                  height: "5rem",
                }}
                as={Link}
                target="__blank"
                onClick={entriesExport}
                color="primary"
                variant="contained"
              >
                EXPORT ENTRIES
              </Button>
            )}

            {entries.length > 0 && total !== 0 && (
              <h2 style={{ textAlign: "center", fontSize: "2.5rem" }}>
                Total # of Entries: {total}
              </h2>
            )}

            {entries.length > 0 && (
              <TableContainer className="Raffle__table">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell> Entry # </TableCell>
                      <TableCell> Reward Date </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {entries.map((entry, i) => (
                      <TableRow key={i}>
                        <TableCell>{entry.entry_no}</TableCell>
                        <TableCell>
                          {format(
                            new Date(entry.reward_date),
                            "MM/dd/yyyy HH:mm"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>

                  <TableFooter>
                    <TableRow>
                      <TablePagination
                        rowsPerPageOptions={PAGE_SIZE_OPTIONS}
                        colSpan={2}
                        count={total}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        SelectProps={{
                          inputProps: {
                            "aria-label": "rows per page",
                          },
                          native: true,
                        }}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                      />
                    </TableRow>
                  </TableFooter>
                </Table>
              </TableContainer>
            )}
          </>
        );
      case 1:
        return (
          <>
            <form className="Raffle__inputs">
              <FormControl
                fullWidth
                style={{ width: "21%", marginTop: "2.5rem" }}
              >
                <InputLabel variant="filled" style={{ fontSize: "2rem" }}>
                  Number of Winners
                </InputLabel>
                <FilledInput
                  value={numOfWinners}
                  onChange={(e) => setNumOfWinners(e.target.value)}
                  label="Number Of Winners"
                  placeholder="Winners Count"
                  style={{
                    height: "6rem",
                    fontSize: "1.5rem",
                    textAlign: "center",
                  }}
                />
              </FormControl>
            </form>

            {raffleDrawLoading ? (
              <div style={{ textAlign: "center", marginTop: "5rem" }}>
                <ScaleLoader
                  loading={raffleDrawLoading}
                  height={50}
                  width={25}
                  margin={2.5}
                  color="#3d5af1"
                />
              </div>
            ) : (
              <>
                <Button
                  style={{
                    width: "50%",
                    margin: "2rem auto 1.5rem",
                    height: "5rem",
                  }}
                  onClick={onRaffleDraw}
                  color="primary"
                  disabled={numOfWinners.length === 0}
                  variant="contained"
                >
                  RAFFLE DRAW
                </Button>
                {winners.length > 0 && (
                  <Button
                    style={{
                      width: "50%",
                      margin: "0 auto 1.5rem",
                      height: "5rem",
                    }}
                    as={Link}
                    onClick={raffleDrawExport}
                    target="__blank"
                    disabled={entries.length === 0 && winners.length === 0}
                    variant="contained"
                  >
                    EXPORT
                  </Button>
                )}
              </>
            )}

            {winners.length > 0 && entries.length > 0 && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell> Entry # </TableCell>
                      <TableCell>Date </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {winners.map((winner, i) => (
                      <TableRow key={i}>
                        <TableCell>{winner.entry_no}</TableCell>
                        <TableCell>
                          {format(
                            new Date(winner.created_at),
                            "MM/dd/yyyy HH:mm"
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {winners.length > 0 && entries.length > 0 && successfulWinners && (
              <Confetti
                width={1650}
                height={1000}
                tweenDuration={500}
                friction={0.98}
                style={{ marginLeft: "258px", marginTop: "500px" }}
              />
            )}
          </>
        );
      default:
        return "";
    }
  };

  return (
    <div className="Raffle">
      <Stepper activeStep={activeStep} alternativeLabel>
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>
      <div className="Raffle__buttons">
        <Button
          onClick={handleBack}
          variant="contained"
          color="secondary"
          style={{ width: "30%" }}
          disabled={activeStep === 0}
        >
          BACK
        </Button>
        {activeStep === 0 && (
          <Button
            onClick={handleNext}
            variant="contained"
            color="primary"
            style={{ width: "30%" }}
            disabled={activeStep === 1}
          >
            NEXT
          </Button>
        )}
        {activeStep === 1 && (
          <Button
            onClick={resetStep}
            variant="contained"
            color="primary"
            style={{ width: "30%" }}
            disabled={winners.length === 0}
          >
            SEARCH ENTRIES AGAIN
          </Button>
        )}
      </div>
      {getStepContent(activeStep)}

      <Snackbar open={saved}>
        <Alert severity="success"> {savedWinners} </Alert>
      </Snackbar>
    </div>
  );
};

export default Raffle;
